import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { repository } from './data'
import { ComponentView } from './ui/ComponentView'
import { FleetView } from './ui/FleetView'
import { IntakeView } from './ui/IntakeView'
import { AdminView } from './ui/AdminView'
import { ScanView } from './ui/ScanView'
import { StepperView } from './ui/StepperView'
import { DevPanel } from './dev/DevPanel'
import { MyHoursView } from './ui/hours/MyHoursView'
import { InventoryView } from './ui/inventory/InventoryView'
import { ItemView } from './ui/inventory/ItemView'
import { ReceiveView } from './ui/receive/ReceiveView'
import { ScheduleView } from './ui/schedule/ScheduleView'
import { Nav } from './ui/shell/Nav'
import { BuildPlace, PeoplePlace } from './ui/shell/Sections'
import { placesFor } from './ui/shell/places'
import { TodayView } from './ui/today/TodayView'
import { WorkView } from './ui/work/WorkView'
import { hubOf, navigate, useRoute } from './ui/router'
import type { Route } from './ui/router'
import type { Me } from './data'
import type { Role } from '@kronos/domain'

/**
 * The shell.
 *
 * **Every screen is a URL.** Places (Today, Fleet, Build…) used to be React
 * state while objects (`/component/K7F38`) were URLs, on the theory that
 * nobody links to a list. In practice a reload — and every launch from a
 * phone's home screen — dropped people on the scanner, and the back button
 * skipped every list they had looked at. Now a place is a path like any other.
 */
export function App(): JSX.Element {
  const route = useRoute()
  const me = useQuery({ queryKey: ['me'], queryFn: () => repository.me() })

  // A 401 is the signed-out state, not a failure. `?next=` carries the
  // destination through Google, which is the whole point when the journey
  // started by pointing a phone at a label.
  if (me.isError && /\b401\b/.test(String(me.error))) return <SignIn />
  // Empty until `me` answers, rather than a guessed default: a nav that shows
  // places and then takes them away is worse than one that arrives a beat late.
  const roles: readonly Role[] = me.data?.roles ?? []
  const places = me.data ? placesFor(roles) : []
  const current = hubOf(route)

  const signOut = (): void => {
    void repository.signOut().finally(() => window.location.assign('/'))
  }

  return (
    <div className="shell">
      <Nav
        places={places}
        current={current}
        person={me.data ? { name: me.data.name, email: me.data.email } : null}
        onSignOut={signOut}
      />
      <main className="shell-main">
        {hubOf(route) === null ? <Back /> : null}
        {me.data ? renderRoute(route, me.data, roles) : null}
      </main>
      {/*
        Lock one on the dev fixtures: a production build resolves `dev/DevPanel`
        to a stub exporting null (`vite.config.ts`), so this branch is not just
        dead — the module is never in the graph. Lock two is inside the panel,
        which asks the *server* whether it is in dev mode.
      */}
      {import.meta.env.DEV && DevPanel ? <DevPanel /> : null}
    </div>
  )
}

function renderRoute(route: Route, me: Me, roles: readonly Role[]): JSX.Element {
  switch (route.name) {
    case 'today':
      return <TodayView me={me} roles={roles} />
    case 'schedule':
      return <ScheduleView />
    case 'hours':
      return <MyHoursView />
    case 'work':
      return <WorkView meId={me.id} roles={roles} />
    case 'receive':
      // Keyed by the item, so "Receive more of these" from an item page starts fresh.
      return <ReceiveView key={route.item ?? ''} initialItem={route.item} />
    case 'inventory':
      return <InventoryView />
    case 'item':
      return <ItemView id={route.id} />
    case 'fleet':
      return <FleetView />
    case 'build':
      return <BuildPlace roles={roles} section={route.section} />
    case 'design':
      // §11.1: the Catalog is the design of the product, not a settings screen.
      return <AdminView roles={roles} />
    case 'people':
      return <PeoplePlace roles={roles} section={route.section} />
    // A robot *is* a component, so both routes render the same page. /robot/:id
    // survives only because labels printed against it may already exist.
    case 'component':
    case 'robot':
      return <ComponentView id={route.id} roles={roles} />
    case 'execution':
      return <StepperView id={route.id} />
    case 'intake':
      return <IntakeView initialSerial={route.serial} />
    case 'scan':
      return route.unknown ? <UnknownLabel serial={route.unknown} /> : <ScanView onResult={(r) => routeForScan(r)} />
  }
}

/**
 * A scan resolves to an object, so it navigates. This is the line that makes
 * "point a phone at a thing and be on that thing's page" true.
 */
function routeForScan(result: { kind: string; id?: string; serial?: string }): void {
  // Both land on the same page: a robot is a component. The scan endpoint still
  // distinguishes them so the scanner can say what it found.
  if ((result.kind === 'robot' || result.kind === 'component') && result.id) {
    navigate({ name: 'component', id: result.id })
  }
  else if (result.kind === 'reserved') navigate({ name: 'intake', serial: result.serial ?? null })
}

function Back(): JSX.Element {
  // Browser back works, but a phone in a workshop is often held one-handed with
  // the other hand on a part, and reaching the system gesture is not free.
  return (
    <button className="ghost back-button" onClick={() => window.history.back()}>
      ← Back
    </button>
  )
}

function UnknownLabel({ serial }: { serial: string | null }): JSX.Element {
  return (
    <div className="stack">
      <h1 className="page-title">Not one of ours</h1>
      <div className="card stack">
        {serial ? <span className="serial">{serial}</span> : null}
        <span className="muted">
          That label is not a Kronos serial, or it has never been printed. Check the label, or take
          the part in if it is new stock.
        </span>
        <button className="primary" onClick={() => navigate({ name: 'intake', serial: null })}>
          Go to intake
        </button>
      </div>
    </div>
  )
}

function SignIn(): JSX.Element {
  const [next, setNext] = useState('/')
  useEffect(() => {
    setNext(`${window.location.pathname}${window.location.search}`)
  }, [])

  return (
    <div
      className="stack"
      style={{ height: '100%', placeContent: 'center', alignItems: 'center', padding: 'var(--pad)' }}
    >
      <img src="/favicon.svg" alt="" width={96} height={96} />
      <h1 className="page-title">Kronos</h1>
      <p className="muted" style={{ textAlign: 'center', maxWidth: '32ch' }}>
        Sign in with your Nupeak account.
      </p>
      <a
        href={`/api/auth/login?next=${encodeURIComponent(next)}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          minHeight: 'var(--tap)',
          padding: '0 24px',
          borderRadius: 'var(--radius)',
          background: 'var(--kronos-blue)',
          color: 'var(--kronos-white)',
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        Continue with Google
      </a>
    </div>
  )
}
